import React, { useState, useEffect, useCallback, useImperativeHandle, forwardRef } from 'react';
import Loading from '../Loading';
import Error from '../Error';
import Results from './Results';
import QuestionPanel from './QuestionPanel';
import Conversation from './Conversation';
import ExerciseLayout from '../shared/ExerciseLayout';
import {
  fetchConversationExercise,
  evaluateConversationWritingExercise,
  recordExerciseAttempt,
  saveExerciseProgress,
  loadExerciseProgress,
  resetExerciseProgress
} from '../../services/api';
import { useExerciseTimer } from '../../hooks/useExerciseTimer';

const ConversationWriteExercise = forwardRef(({ topic = "groceries", onBack }, ref) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(1);
  const [userAnswers, setUserAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [conversation, setConversation] = useState([]);
  const [evaluations, setEvaluations] = useState({});
  const [isEvaluating, setIsEvaluating] = useState(false);

  const { stop: stopTimer } = useExerciseTimer();

  useImperativeHandle(ref, () => ({
    saveProgress: async () => {
      try {
        await saveExerciseProgress(topic, 'conversation_writing', {
          currentQuestionIndex,
          userAnswers,
          evaluations,
          showResults
        });
      } catch (err) {
        console.error('Error saving progress from ref:', err);
        throw err;
      }
    }
  }));

  const fetchExercises = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await fetchConversationExercise({topic: topic});

      setConversation(data.dialogue);
      setQuestions(data.questions);

    } catch (err) {
      console.error('Error fetching conversation:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [topic]);

  useEffect(() => {
    fetchExercises();
  }, [fetchExercises]);

  useEffect(() => {
    const loadProgress = async () => {
      if (questions.length > 0) {
        try {
          const progressData = await loadExerciseProgress(topic, 'conversation_writing');

          if (progressData.progress_data) {
            const { currentQuestionIndex: savedIndex, userAnswers: savedAnswers, evaluations: savedEvaluations, showResults: savedShowResults } = progressData.progress_data;

            if (savedIndex) setCurrentQuestionIndex(savedIndex);
            if (savedAnswers) setUserAnswers(savedAnswers);
            if (savedEvaluations) setEvaluations(savedEvaluations);
            if (savedShowResults) setShowResults(true);
          }
        } catch (err) {
          console.error('Error loading progress:', err);
        }
      }
    };

    loadProgress();
  }, [questions, topic]);

  const handleAnswerChange = (e) => {
    setUserAnswers({
      ...userAnswers,
      [currentQuestionIndex]: e.target.value
    });
  };

  const getConversationContext = useCallback(() => {
    return conversation
      .map((line, idx) => `${idx + 1}. ${line}`)
      .join('\n');
  }, [conversation]);

  const submitAnswer = async () => {
    const currentQuestion = questions.find(q => q.id === currentQuestionIndex);
    const userAnswer = userAnswers[currentQuestionIndex] || '';

    try {
      setIsEvaluating(true);

      const result = await evaluateConversationWritingExercise(
        getConversationContext(),
        currentQuestion,
        userAnswer
      );

      setEvaluations(prev => ({
        ...prev,
        [currentQuestionIndex]: result
      }));

      try {
        await saveExerciseProgress(topic, 'conversation_writing', {
          currentQuestionIndex,
          userAnswers,
          evaluations: {
            ...evaluations,
            [currentQuestionIndex]: result
          },
          showResults
        });
      } catch (err) {
        console.error('Error saving progress:', err);
      }

    } catch (err) {
      console.error('Error evaluating answer:', err);
      setError(err.message);
    } finally {
      setIsEvaluating(false);
    }
  };

  const next = async () => {
    if (currentQuestionIndex < questions.length) {
      const nextIndex = currentQuestionIndex + 1;
      setCurrentQuestionIndex(nextIndex);

      try {
        await saveExerciseProgress(topic, 'conversation_writing', {
          currentQuestionIndex: nextIndex,
          userAnswers,
          evaluations,
          showResults
        });
      } catch (err) {
        console.error('Error saving progress:', err);
      }
    }
  };

  const back = () => {
    if (currentQuestionIndex > 1) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const finishExercise = async () => {
    try {
      const finalTime = stopTimer();

      const evaluationsArray = questions.map(q => {
        const evaluation = evaluations[q.id];
        return evaluation || {
          question_id: q.id,
          evaluation: 'Neodpovezeno',
          is_correct: false
        };
      });

      const correctCount = evaluationsArray.filter(e => e.is_correct).length;
      const totalCount = questions.length;

      const errors = evaluationsArray
        .filter(e => !e.is_correct)
        .map(e => ({
          category: 'comprehension',
          question_id: e.question_id,
          details: e.evaluation
        }));

      try {
        await recordExerciseAttempt({
          topic: topic,
          exercise_type: 'conversation_writing',
          score: correctCount,
          max_score: totalCount,
          time_spent_seconds: finalTime,
          errors: errors.length > 0 ? errors : null
        });
      } catch (trackingError) {
        console.error('Failed to record exercise attempt:', trackingError);
      }

      setEvaluations(evaluationsArray);
      setShowResults(true);

      try {
        await saveExerciseProgress(topic, 'conversation_writing', {
          currentQuestionIndex,
          userAnswers,
          evaluations: evaluationsArray,
          showResults: true
        });
      } catch (err) {
        console.error('Error saving progress on finish:', err);
      }
    } catch (err) {
      console.error('Error finishing exercise:', err);
      setError(err.message);
    }
  };

  const restart = async () => {
    setUserAnswers({});
    setCurrentQuestionIndex(1);
    setShowResults(false);
    setEvaluations({});

    try {
      await resetExerciseProgress(topic, 'conversation_writing');
    } catch (err) {
      console.error('Error resetting progress:', err);
    }

    await fetchExercises();
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
    }
  };

  if (loading) {
    return <Loading />;
  }

  if (error) {
    return <Error error={error} />;
  }

  if (showResults) {
    const evaluationsArray = Array.isArray(evaluations)
      ? evaluations
      : questions.map(q => evaluations[q.id] || {
          question_id: q.id,
          evaluation: 'Neodpovezeno',
          is_correct: false
        });

    return (
      <Results
        questions={questions}
        userAnswers={userAnswers}
        evaluations={evaluationsArray}
        restart={restart}
        onBack={handleBack}
        topic={topic}
      />
    );
  }

  return (
    <ExerciseLayout
      title="Konverzace - Dialogy"
      subtitle="Odpovídejte na otázky k dialogu"
      color="blue"
      icon="💬"
      progress={{ current: currentQuestionIndex, total: questions.length }}
      onBack={handleBack}
      twoColumn={true}
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Dialog - leva strana */}
        <div className="lg:sticky lg:top-24 lg:self-start">
          <h5 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-3 text-center">Dialog</h5>
          <Conversation conversation={conversation} topic={topic} />
        </div>

        {/* Otázky - pravá strana */}
        <div>
          <h5 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-3 text-center">Otázky</h5>
          <QuestionPanel
            currentQuestionIndex={currentQuestionIndex}
            questions={questions}
            userAnswers={userAnswers}
            handleAnswerChange={handleAnswerChange}
            back={back}
            next={next}
            submitAnswer={submitAnswer}
            evaluation={evaluations[currentQuestionIndex]}
            isEvaluating={isEvaluating}
            isLastQuestion={currentQuestionIndex === questions.length}
            onFinish={finishExercise}
            topic={topic}
          />
        </div>
      </div>
    </ExerciseLayout>
  );
});

export default ConversationWriteExercise;
